/**
 * Presenter: queue of signed-in viewers who raised ✋ until the presenter dismisses the panel.
 *
 * With `sender_uid`: one row per account — ✋ toggles that row on/off from any remote tab (same logical hand).
 * Without `sender_uid` (legacy): one row per MQTT participant_id, toggle per tab.
 */

import type { RemoteV1EmotePayload } from '../../services/remote/v1/protocol';

export const REMOTE_V1_RAISE_HAND_EMOJI = '\u270B'; /* ✋ */

const ROOT_CLASS = 'lectura-remote-raise-hand-root';
const MAX_VISIBLE = 3;

function getOverlayParent(): HTMLElement {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  const fs = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  if (fs && fs instanceof HTMLElement) return fs;
  return document.body;
}

let fullscreenBound = false;
function bindFullscreenReparent(): void {
  if (fullscreenBound || typeof document === 'undefined') return;
  fullscreenBound = true;
  const onFs = (): void => {
    const root = document.querySelector<HTMLElement>(`.${ROOT_CLASS}`);
    if (!root) return;
    const parent = getOverlayParent();
    if (root.parentElement !== parent) parent.appendChild(root);
  };
  document.addEventListener('fullscreenchange', onFs);
  document.addEventListener('webkitfullscreenchange', onFs as EventListener);
}

type Queued = { dedupeKey: string; displayName: string };

let queue: Queued[] = [];
let rootEl: HTMLDivElement | null = null;

function ensureRoot(): HTMLDivElement {
  bindFullscreenReparent();
  const parent = getOverlayParent();
  let root = document.querySelector<HTMLDivElement>(`.${ROOT_CLASS}`);
  if (!root) {
    root = document.createElement('div');
    root.className = ROOT_CLASS;
    root.setAttribute('aria-live', 'polite');
  }
  if (root.parentElement !== parent) parent.appendChild(root);
  rootEl = root;
  return root;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function render(): void {
  if (queue.length === 0) {
    rootEl?.remove();
    rootEl = null;
    return;
  }

  const root = ensureRoot();
  const visible = queue.slice(0, MAX_VISIBLE);
  const others = Math.max(0, queue.length - MAX_VISIBLE);
  const othersLine =
    others > 0
      ? `<p class="lectura-remote-raise-hand-others">and ${others} other ${others === 1 ? 'person' : 'people'}</p>`
      : '';

  root.innerHTML = `
    <div class="lectura-remote-raise-hand-panel" role="button" tabindex="0" aria-label="Raised hands — click to dismiss">
      <span class="lectura-remote-raise-hand-title">Raised hands</span>
      <ul class="lectura-remote-raise-hand-list" role="list">
        ${visible
          .map(
            (q) =>
              `<li class="lectura-remote-raise-hand-item"><span class="lectura-remote-raise-hand-icon" aria-hidden="true">${REMOTE_V1_RAISE_HAND_EMOJI}</span> <span class="lectura-remote-raise-hand-name">${escapeHtml(q.displayName)}</span></li>`
          )
          .join('')}
      </ul>
      ${othersLine}
      <span class="lectura-remote-raise-hand-hint">Tap to clear</span>
    </div>
  `;

  const panel = root.querySelector<HTMLElement>('.lectura-remote-raise-hand-panel');
  const dismiss = (): void => {
    clearRaiseHandQueue();
  };
  panel?.addEventListener('click', (e) => {
    e.preventDefault();
    dismiss();
  });
  panel?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      dismiss();
    }
  });
}

export function isRaiseHandEmotePayload(em: RemoteV1EmotePayload): boolean {
  return String(em.emoji || '').trim() === REMOTE_V1_RAISE_HAND_EMOJI;
}

/**
 * @returns true if handled as raise-hand queue (caller skips floating emote toast).
 */
export function handlePresenterRaiseHandEmote(
  em: RemoteV1EmotePayload,
  participantId: string | undefined
): boolean {
  if (!isRaiseHandEmotePayload(em)) return false;
  const name = typeof em.sender_name === 'string' ? em.sender_name.trim() : '';
  const pid = participantId?.trim();
  if (!name || !pid) return false;

  const name80 = name.slice(0, 80);
  const uidRaw = typeof em.sender_uid === 'string' ? em.sender_uid.trim() : '';

  if (uidRaw) {
    const key = `uid:${uidRaw}`;
    const i = queue.findIndex((q) => q.dedupeKey === key);
    if (i >= 0) {
      queue.splice(i, 1);
    } else {
      queue.push({ dedupeKey: key, displayName: name80 });
    }
  } else {
    const key = `pid:${pid}`;
    const j = queue.findIndex((q) => q.dedupeKey === key);
    if (j >= 0) queue.splice(j, 1);
    else queue.push({ dedupeKey: key, displayName: name80 });
  }

  render();
  return true;
}

export function clearRaiseHandQueue(): void {
  queue = [];
  rootEl?.remove();
  rootEl = null;
}
